/**
 * ZONIY - Part Detail Page
 * @module PartDetailPage
 * @author Saif Ali Khan - Full Stack Developer
 * @description Individual part details and purchase
 * @version 1.0.0
 * Git: Saif Ali Khan <saif@linesoft.com> - Part detail view
 */

import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ShoppingCart, Heart, ArrowLeft, MapPin, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import Layout from '../components/Layout';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PartDetailPage = () => {
  const { partId } = useParams();
  const { addToCart } = useCart();
  const { token } = useAuth();
  const [part, setPart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);

  useEffect(() => {
    fetchPart();
  }, [partId]);

  const fetchPart = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/parts/${partId}`);
      setPart(response.data);
    } catch (error) {
      console.error('Failed to fetch part:', error);
      toast.error('Part not found');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = async () => {
    const success = await addToCart(partId, quantity);
    if (success) {
      setQuantity(1);
    }
  };

  const handleAddToFavorites = async () => {
    if (!token) {
      toast.error('Please login to add favorites');
      return;
    }
    try {
      await axios.post(`${API_URL}/api/favorites/${partId}`);
      toast.success('Added to favorites');
    } catch (error) {
      toast.error('Failed to add to favorites');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse">
            <div className="h-8 w-32 bg-slate-200 dark:bg-slate-800 mb-8" />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div className="aspect-square bg-slate-200 dark:bg-slate-800" />
              <div className="space-y-4">
                <div className="h-6 bg-slate-200 dark:bg-slate-800 w-32" />
                <div className="h-10 bg-slate-200 dark:bg-slate-800 w-3/4" />
                <div className="h-24 bg-slate-200 dark:bg-slate-800" />
              </div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!part) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <AlertCircle className="w-16 h-16 text-slate-400 mx-auto mb-4" />
          <h2 className="font-heading font-bold text-2xl text-slate-900 dark:text-white mb-2">
            Part Not Found
          </h2>
          <p className="text-slate-600 dark:text-slate-400 mb-6">
            The part you're looking for doesn't exist or has been removed.
          </p>
          <Link to="/search">
            <Button className="bg-blue-600 hover:bg-blue-700">
              Back to Search
            </Button>
          </Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="part-detail-page">
        {/* Back Link */}
        <Link 
          to="/search" 
          className="inline-flex items-center gap-2 text-slate-600 dark:text-slate-400 hover:text-blue-600 mb-8"
          data-testid="back-link"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Search
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Image */}
          <div className="bg-slate-100 dark:bg-slate-800 aspect-square flex items-center justify-center overflow-hidden">
            {part.image_url ? (
              <img 
                src={part.image_url} 
                alt={part.name} 
                className="w-full h-full object-cover"
                data-testid="part-image"
              />
            ) : (
              <div className="text-center p-8">
                <span className="font-mono text-2xl text-slate-400">{part.part_number}</span>
                <p className="text-slate-500 mt-2">No image available</p>
              </div>
            )}
          </div>

          {/* Details */}
          <div>
            <div className="font-mono text-sm text-slate-500 uppercase tracking-wider mb-2" data-testid="part-number">
              {part.part_number}
            </div>
            <h1 className="font-heading font-black text-4xl text-slate-900 dark:text-white uppercase tracking-tight mb-4" data-testid="part-name">
              {part.name}
            </h1>
            
            <div className="flex items-center gap-4 mb-6">
              <span className={`px-3 py-1 text-sm font-bold uppercase ${
                part.condition === 'new' ? 'bg-green-100 text-green-800' :
                part.condition === 'refurbished' ? 'bg-blue-100 text-blue-800' :
                'bg-slate-100 text-slate-800'
              }`} data-testid="part-condition">
                {part.condition}
              </span>
              <span className={`px-3 py-1 text-sm font-bold uppercase ${
                part.quantity > 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
              }`} data-testid="part-stock">
                {part.quantity > 0 ? `${part.quantity} In Stock` : 'Out of Stock'}
              </span>
            </div>

            <div className="font-heading font-black text-5xl text-blue-600 mb-6" data-testid="part-price">
              ${part.price.toFixed(2)}
            </div>

            {part.description && (
              <p className="text-slate-600 dark:text-slate-400 mb-6 leading-relaxed" data-testid="part-description">
                {part.description}
              </p>
            )}

            {/* Vehicle Compatibility */}
            {(part.make || part.model) && (
              <div className="bg-slate-50 dark:bg-slate-800 p-4 border border-slate-200 dark:border-slate-700 mb-6">
                <h3 className="font-heading font-bold text-sm uppercase text-slate-900 dark:text-white mb-2">
                  Vehicle Compatibility
                </h3>
                <div className="flex items-center gap-2 text-slate-700 dark:text-slate-300">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  {part.make} {part.model}
                  {part.year_from && (
                    <span className="text-slate-500">
                      ({part.year_from} - {part.year_to || 'Present'})
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Vendor Info */}
            {part.vendor_name && (
              <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400 mb-6">
                <MapPin className="w-4 h-4" />
                <span>Sold by: <strong>{part.vendor_name}</strong></span>
                {part.location && <span>• {part.location}</span>}
              </div>
            )}

            {/* Add to Cart */}
            {part.quantity > 0 ? (
              <div className="flex items-center gap-4">
                <div className="flex items-center">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="w-10 h-10 bg-slate-100 dark:bg-slate-800 border border-slate-300 dark:border-slate-600 flex items-center justify-center hover:bg-slate-200 dark:hover:bg-slate-700"
                    data-testid="qty-decrease"
                  >
                    -
                  </button>
                  <Input
                    type="number"
                    min="1"
                    max={part.quantity}
                    value={quantity}
                    onChange={(e) => setQuantity(Math.min(part.quantity, Math.max(1, parseInt(e.target.value) || 1)))}
                    className="w-16 h-10 text-center border-y border-slate-300 dark:border-slate-600 rounded-none"
                    data-testid="qty-input"
                  />
                  <button
                    onClick={() => setQuantity(Math.min(part.quantity, quantity + 1))}
                    className="w-10 h-10 bg-slate-100 dark:bg-slate-800 border border-slate-300 dark:border-slate-600 flex items-center justify-center hover:bg-slate-200 dark:hover:bg-slate-700"
                    data-testid="qty-increase"
                  >
                    +
                  </button>
                </div>
                <Button 
                  onClick={handleAddToCart}
                  className="flex-1 h-12 btn-industrial-orange"
                  data-testid="add-to-cart-btn"
                >
                  <ShoppingCart className="w-5 h-5 mr-2" />
                  Add to Cart
                </Button>
                <Button 
                  variant="outline" 
                  size="icon"
                  className="h-12 w-12"
                  onClick={handleAddToFavorites}
                  data-testid="add-to-favorites-btn"
                >
                  <Heart className="w-5 h-5" />
                </Button>
              </div>
            ) : (
              <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-200">
                This part is currently out of stock.
              </div>
            )}

            {/* Category & Tags */}
            <div className="mt-8 pt-6 border-t border-slate-200 dark:border-slate-700">
              <div className="flex flex-wrap gap-2">
                {part.category && (
                  <Link 
                    to={`/search?category=${encodeURIComponent(part.category)}`}
                    className="px-3 py-1 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 text-sm hover:bg-slate-200 dark:hover:bg-slate-700"
                  >
                    {part.category}
                  </Link>
                )}
                {part.make && (
                  <Link 
                    to={`/search?make=${encodeURIComponent(part.make)}`}
                    className="px-3 py-1 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 text-sm hover:bg-slate-200 dark:hover:bg-slate-700"
                  >
                    {part.make}
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default PartDetailPage;
